import React from 'react'
import PropTypes from 'prop-types'

// The one button of the group-leader apps: four tones at three sizes.

const VARIANT_CLASS = {
  primary: 'gl-btn--primary',
  secondary: 'gl-btn--secondary',
  outlinePrimary: 'gl-btn--outline-primary',
  danger: 'gl-btn--danger',
}

const SIZE_CLASS = {
  lg: 'gl-btn--lg',
  md: 'gl-btn--md',
  sm: 'gl-btn--sm',
}

const Button = ({
  children,
  variant = 'secondary',
  size = 'sm',
  wide = false,
  href,
  onClick,
  disabled = false,
  loading = false,
  ariaLabel,
}) => {
  // An icon-only button is unusable with a screen reader without a label (a11y checklist §4/§5).
  if (process.env.NODE_ENV !== 'production' && !children && !ariaLabel) {
    // eslint-disable-next-line no-console
    console.warn('gl Button: a button without children needs an ariaLabel')
  }

  // An <a> has no disabled state, so `disabled` with href would leave a link that still navigates.
  if (process.env.NODE_ENV !== 'production' && href && disabled) {
    // eslint-disable-next-line no-console
    console.warn('gl Button: href ignores `disabled`; render a <button> instead')
  }

  const classes = [
    'gl-btn',
    VARIANT_CLASS[variant] || VARIANT_CLASS.secondary,
    SIZE_CLASS[size] || SIZE_CLASS.sm,
    wide ? 'gl-btn--wide' : '',
  ].filter(Boolean).join(' ')

  // Navigation stays an anchor: a <button> that changes location loses middle-click and its role.
  if (href) {
    return (
      <a href={href} className={classes} aria-label={ariaLabel}>
        {children}
      </a>
    )
  }

  // A spinner, not just a greyed button, while the work runs: the last step waits on several requests.
  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      >
      {loading && <span className="gl-btn__spinner" aria-hidden="true"/>}
      {children}
    </button>
  )
}

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([ 'primary', 'secondary', 'outlinePrimary', 'danger' ]),
  size: PropTypes.oneOf([ 'lg', 'md', 'sm' ]),
  wide: PropTypes.bool,
  // Renders an <a> instead of a <button>; `onClick`/`disabled` do not apply
  href: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  // Shows a spinner in the button and disables it; does not apply to `href`
  loading: PropTypes.bool,
  ariaLabel: PropTypes.string,
}

export default Button
